import '../SettingsPage.scss';
import { Button, Checkbox, Icon, Input } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierLine, ModifierFull, UnarchiveFilled, UnarchiveLine } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useFinancialBenefitsPageVM } from './useFinancialBenefitsPageVM';
import { Formik } from 'formik';
import { FinancialBenefit, FinancialBenefitToCreate } from '@services/member/list/financial-benefits/financial-benefits.types';
import { formatMoney } from '@utils/numbers';

export default function FinancialBenefitsPage() {
    const {
        mutationHook,
        isArchived,
        handleMetadata,
        handleArchiveFinancialBenefits,
        needReload,
        setIsArchived,
        updateFinancialBenefit,
        createFinancialBenefit,
        MySwal
    } = useFinancialBenefitsPageVM();

    const columnHelper = createColumnHelper<FinancialBenefit>();
    const navigate = useNavigate();

    const handleModificationFinancialBenefits = async (financialBenefit: FinancialBenefitToCreate | null, id?: number) => {
        await MySwal.fire<FinancialBenefitToCreate>({
            title: financialBenefit ? "Modification d'un type de prestation financière" : "Création d'un type de prestation financière",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: financialBenefit ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{
                        code: financialBenefit?.code || '',
                        label: financialBenefit?.label || '',
                        amount: financialBenefit?.amount || 0,
                        observation: financialBenefit?.observation || '',
                        accountNumber: financialBenefit?.accountNumber || ''
                    }}
                    onSubmit={(values) => {
                        if (id) {
                            updateFinancialBenefit({
                                id: id,
                                code: values.code,
                                label: values.label,
                                amount: values.amount,
                                observation: values.observation,
                                accountNumber: values.accountNumber,
                                isArchived: false
                            });
                        } else {
                            createFinancialBenefit({
                                code: values.code,
                                label: values.label,
                                amount: values.amount,
                                observation: values.observation,
                                accountNumber: values.accountNumber,
                                isArchived: false
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <form id="form-financial-benefit" onSubmit={handleSubmit}>
                            <Input name="code" label="Code" required placeholder="Code" value={values.code} onChange={handleChange} />
                            <Input name="label" label="Libellé" required placeholder="Libellé" value={values.label} onChange={handleChange} />
                            <Input
                                type="number"
                                rightContent={'€'}
                                name="amount"
                                required
                                label="Montant"
                                placeholder="Montant"
                                value={values.amount}
                                onChange={handleChange}
                            />
                            <Input
                                name="observation"
                                label="Observation"
                                placeholder="Observation"
                                value={values.observation}
                                onChange={handleChange}
                            />
                            <Input
                                name="accountNumber"
                                label="Numéro de compte"
                                required
                                placeholder="Numéro de compte"
                                value={values.accountNumber}
                                onChange={handleChange}
                            />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-financial-benefit') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('code', {
            header: 'Code',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('label', {
            header: 'Libellé',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('amount', {
            header: 'Montant',
            enableSorting: true,
            cell: (info) => {
                return <>{formatMoney(info.getValue())}</>;
            }
        }),
        columnHelper.accessor('observation', {
            header: 'Observation',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('accountNumber', {
            header: 'Numéro de compte',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('id', {
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationFinancialBenefits(
                                    {
                                        code: info.row.original.code,
                                        label: info.row.original.label,
                                        amount: info.row.original.amount,
                                        observation: info.row.original.observation,
                                        accountNumber: info.row.original.accountNumber,
                                        isArchived: false
                                    },
                                    info.row.original.id
                                );
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleArchiveFinancialBenefits([info.row.original], !isArchived);
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<FinancialBenefit>[];

    return (
        <>
            <div className="settings-tabs-content financial-benefits">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    mutationHook={mutationHook}
                    onRowClick={(row) => navigate(`/members-management/settings/financial-benefits/${row.id}`)}
                    defaultSorting={{
                        field: 'code',
                        direction: 'desc'
                    }}
                    onMetadata={handleMetadata}
                    createButtonOnClick={() => handleModificationFinancialBenefits(null)}
                    createButtonLabel="Créer un type de prestation financière"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    needReload={needReload}
                    deleting={{
                        enabled: isArchived,
                        entity: 'financial_benefit_type',
                    }}
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : 'Archiver',
                                icon: isArchived ? <UnarchiveLine /> : <ArchiveLine />,
                                hoverIcon: isArchived ? <UnarchiveFilled /> : <ArchiveFilled />,
                                onClick: (lines: FinancialBenefit[]) => handleArchiveFinancialBenefits(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </div>
        </>
    );
}
